import type { enums } from "@fraction/shared";
import {
  closeDetachedWindow,
  type DetachedWindowPrewarmOptions,
  openDetachedWindow,
  prewarmDetachedWindow,
} from "src/lib/runtime/detachedWindows";
import { isTauriRuntime } from "src/lib/runtime/environment";

export const APPLICATION_CHAT_SURFACE = "application-chat";
const APPLICATION_CHAT_OWNERSHIP_EVENT = "fraction:application-chat:ownership";
const interactedApplicationChats = new Set<string>();

export interface ApplicationChatWindowPayload {
  applicationId: string;
  chatType: enums.ChatType;
  applicantNames?: string;
  applicationEid?: string;
}

const APPLICATION_CHAT_WINDOW_OPTIONS: DetachedWindowPrewarmOptions = {
  surface: APPLICATION_CHAT_SURFACE,
  title: "Application Chat",
  width: 420,
  height: 560,
  minWidth: 420,
  minHeight: 440,
  placement: "beside-main",
};

function applicationChatInstanceKey(payload: ApplicationChatWindowPayload): string {
  return `application:${payload.applicationId}:chat:${payload.chatType}`;
}

function interactionStorageKey(payload: ApplicationChatWindowPayload): string {
  return `fraction:application-chat-interacted:${payload.applicationId}:${payload.chatType}`;
}

function interactionKey(payload: ApplicationChatWindowPayload): string {
  return `${payload.applicationId}:${payload.chatType}`;
}

export function prewarmApplicationChatWindow(): Promise<void> {
  return prewarmDetachedWindow(APPLICATION_CHAT_WINDOW_OPTIONS);
}

export function markApplicationChatInteracted(payload: ApplicationChatWindowPayload): void {
  interactedApplicationChats.add(interactionKey(payload));
  localStorage.setItem(interactionStorageKey(payload), "1");
}

export function clearApplicationChatInteraction(payload: ApplicationChatWindowPayload): void {
  interactedApplicationChats.delete(interactionKey(payload));
  localStorage.removeItem(interactionStorageKey(payload));
}

async function emitApplicationChatOwnership(
  payload: ApplicationChatWindowPayload,
  interacted: boolean
): Promise<void> {
  if (!isTauriRuntime()) return;
  const { emitTo } = await import("@tauri-apps/api/event");
  await emitTo("main", APPLICATION_CHAT_OWNERSHIP_EVENT, { payload, interacted });
}

export function reportApplicationChatInteraction(payload: ApplicationChatWindowPayload): void {
  markApplicationChatInteracted(payload);
  void emitApplicationChatOwnership(payload, true).catch((error) =>
    console.warn("Could not report application chat interaction to the main window.", error)
  );
}

export async function listenForApplicationChatOwnership(): Promise<() => void> {
  if (!isTauriRuntime()) return () => undefined;
  const { listen } = await import("@tauri-apps/api/event");
  return listen<{ payload: ApplicationChatWindowPayload; interacted: boolean }>(
    APPLICATION_CHAT_OWNERSHIP_EVENT,
    (event) => {
      if (event.payload.interacted) markApplicationChatInteracted(event.payload.payload);
      else clearApplicationChatInteraction(event.payload.payload);
    }
  );
}

export async function handleNativeApplicationChatCloseRequest(
  payload: ApplicationChatWindowPayload,
  event: { preventDefault: () => void },
  nativeWindow: { destroy: () => Promise<void> }
): Promise<void> {
  event.preventDefault();
  clearApplicationChatInteraction(payload);
  await emitApplicationChatOwnership(payload, false).catch(() => undefined);
  await nativeWindow.destroy();
}

export function applicationChatHasInteraction(payload: ApplicationChatWindowPayload): boolean {
  return (
    interactedApplicationChats.has(interactionKey(payload)) ||
    localStorage.getItem(interactionStorageKey(payload)) === "1"
  );
}

export async function openDetachedApplicationChatWindow(
  payload: ApplicationChatWindowPayload,
  { focus = true }: { focus?: boolean } = {}
): Promise<string> {
  return openDetachedWindow({
    ...APPLICATION_CHAT_WINDOW_OPTIONS,
    instanceKey: applicationChatInstanceKey(payload),
    payload,
    title: payload.applicantNames
      ? `Chat — ${payload.applicantNames}`
      : payload.applicationEid
        ? `Chat — ${payload.applicationEid}`
        : "Application Chat",
    focus,
  });
}

/** Close an auto-opened chat only while it is still untouched. */
export async function closePristineApplicationChatWindow(
  payload: ApplicationChatWindowPayload
): Promise<boolean> {
  if (applicationChatHasInteraction(payload)) return false;
  return closeDetachedWindow(APPLICATION_CHAT_SURFACE, applicationChatInstanceKey(payload));
}
